import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';

export const editPermissionsNs = 'entities';
export const editPermissionsKeyPrefix = 'edit_permissions';
export const useEditPermissionsTranslation = (): UseTranslationResponse<
  typeof editPermissionsNs,
  typeof editPermissionsKeyPrefix
> => (
  useTranslation(editPermissionsNs, { keyPrefix: editPermissionsKeyPrefix })
);

export const ruRu = (__LOCALE_RU__ ? ({
  [editPermissionsKeyPrefix]: {
    title: 'Доступы',
    save: 'Сохранить',
    reset: 'Сбросить изменения',
    check_all: 'Выбрать все',
    system_objects: {
      title: 'Объекты системы',
      users: 'Пользователи',
      roles: 'Роли',
      departments: 'Подразделения',
      permission_tags: 'Теги доступа',
      face: 'Лица',
      card: 'Карточки',
      card_document: 'Файлы карточек',
      stream: 'Источники событий',
      group: 'Группы источников',
      handler: 'Сценарии',
      event_fs: 'События (обнаружение, совпадение)',
      alarm: 'Статус тревожного события',
      list: 'Списки (карточек)',
      task: 'Задачи',
      notification: 'Уведомления',
      audit_log: 'Журнал логов',
      audit_config: 'Настройка журналирования',
      session: 'Пользовательские сессии',
      card_custom_field: 'Динамические поля карточек',
      event_send_rule: 'Правила отправки событий в другие департаменты',
    },
    pages: {
      title: 'Интерфейс системы',
      operator_panel: 'Панель оператора',
      events_fs_archive: 'Архив событий / Обнаружение и сопадение',
      events_va_archive: 'Архив событий / Видеоаналитики',
      events_cars_archive: 'Архив событий / Транспорт',
      event_fs_details: 'Детали событий обнаружение и совпадение',
      event_va_details: 'Детали событий видеоаналитик',
      event_cars_details: 'Детали событий транспорт',
      videowall: 'Видеостена',
      search: 'Поиск',
      statistics_fs: 'Статистика',
      cards: 'Картотека и списки',
      cards_details: 'Карточка персоны',
      lists: 'Список',
      streams_fs: 'Источники / Лица и тела',
      streams_va: 'Источники событий',
      handlers: 'Сценарии',
      handler_details: 'Детали сценария',
      tasks: 'Задачи',
      monitoring: 'Мониторинг',
      licenses: 'Лицензии',
      notifications: 'Уведомления',
      departments: 'Подразделения',
      users: 'Пользователи',
      roles: 'Роли',
      permission_tags: 'Теги доступа',
    },
    permission: {
      read: 'Просмотр',
      create: 'Создание',
      update: 'Изменение',
      delete: 'Удаление',
      available: 'Доступно',
    },
    visibility: {
      title: 'Зона видимости',
      options: {
        basic: 'Обычная',
        local: 'Локальная',
        global: 'Глобальная',
      },
      descriptions: {
        basic: 'Доступ только к спискам и тегам доступа, которые «Назначены всем в подразделении».',
        local: 'Доступ ко всем спискам и тегам доступа подразделения (в том числе тем которые не «Назначены всем»)',
        global: 'Неограниченный доступ, не зависимо от подразделений.',
      },
    },
    reset_confirmation: {
      title: 'Сбросить изменения',
      description: 'Вы уверены, что хотите сбросить все доступы к состоянию на момент последнего сохранения?',
      confirm: 'Сбросить',
      reject: 'Отмена',
    },
  },
}) : {}) as Record<string, unknown>;
export const en = (__LOCALE_EN__ ? ({
  [editPermissionsKeyPrefix]: {
    title: 'Accesses',
    save: 'Save',
    reset: 'Reset changes',
    check_all: 'Select all',
    system_objects: {
      title: 'System objects',
      users: 'Users',
      roles: 'Roles',
      departments: 'Departments',
      permission_tags: 'Permission Tags',
      face: 'Faces',
      card: 'Cards',
      card_document: 'Card Files',
      stream: 'Event sources',
      group: 'Source groups',
      handler: 'Handlers',
      event_fs: 'Events (detection, match)',
      alarm: 'Alarm event status',
      list: 'Lists (of cards)',
      task: 'Tasks',
      notification: 'Notifications',
      audit_log: 'Audit log',
      audit_config: 'Audit settings',
      session: 'User sessions',
      card_custom_field: 'Dynamic card fields',
      event_send_rule: 'Rules for sending events to other departments',
    },
    pages: {
      title: 'System interface',
      operator_panel: 'Operator panel',
      events_fs_archive: 'Event archive / Detection and Match',
      events_va_archive: 'Event archive / Video analytics',
      events_cars_archive: 'Event archive / Transport',
      event_fs_details: 'Detection and matching event details',
      event_va_details: 'Video analytics event details',
      event_cars_details: 'Transport event details',
      videowall: 'Videowall',
      search: 'Search',
      statistics_fs: 'Statistics',
      cards: 'Cards an lists',
      cards_details: 'Person card',
      lists: 'List',
      streams_fs: 'Sources / Face and bodies',
      streams_va: 'Event sources',
      handlers: 'Handlers',
      handler_details: 'Handler details',
      tasks: 'Tasks',
      monitoring: 'Monitoring',
      licenses: 'Licenses',
      notifications: 'Notifications',
      departments: 'Departments',
      users: 'Users',
      roles: 'Roles',
      permission_tags: 'Permission Tags',
    },
    permission: {
      read: 'View',
      create: 'Creation',
      update: 'Change',
      delete: 'Deletion',
      available: 'Available',
    },
    visibility: {
      title: 'Visibility zone',
      options: {
        basic: 'Basic',
        local: 'Local',
        global: 'Global',
      },
      descriptions: {
        basic: 'Access only to lists and access tags assigned to everyone in the department.',
        local: 'Access to all lists and access tags of the department (including those not assigned to everyone).',
        global: 'Unlimited access, regardless of departments.',
      },
    },
    reset_confirmation: {
      title: 'Reset changes',
      description: 'Are you sure you want to reset all accesses to the state at the time of the last save?',
      confirm: 'Reset',
      reject: 'Cancel',
    },
  },
}) : {}) as Record<string, unknown>;
export const esEs = (__LOCALE_ESES__ ? ({
  [editPermissionsKeyPrefix]: {
    title: 'Accesos',
    save: 'Ahorrar',
    reset: 'Restablecer cambios',
    check_all: 'Seleccionar todo',
    system_objects: {
      title: 'Objetos del sistema',
      users: 'Usuarios',
      roles: 'Roles',
      departments: 'Departamentos',
      permission_tags: 'Etiquetas de acceso',
      face: 'Caras',
      card: 'Tarjetas',
      card_document: 'Archivos de tarjetas',
      stream: 'Fuentes de eventos',
      group: 'Grupos de fuentes',
      handler: 'Escenarios',
      event_fs: 'Eventos (detección, coincidencia)',
      alarm: 'Estado del evento de alarma',
      list: 'Listas (de cartas)',
      task: 'Tareas',
      notification: 'Notificaciones',
      audit_log: 'Registro de actividad',
      audit_config: 'Configurar el registro',
      session: 'Sesiones de usuario',
      card_custom_field: 'Campos de tarjetas dinámicos',
      event_send_rule: 'Normas para enviar eventos a otros departamentos',
    },
    pages: {
      title: 'Interfaz del sistema',
      operator_panel: 'Panel del operador',
      events_fs_archive: 'Archivo de eventos / Detección y confluencia',
      events_va_archive: 'Archivo de eventos / Análisis de vídeo',
      events_cars_archive: 'Archivo de eventos / Transporte',
      event_fs_details: 'Detección y coincidencia de detalles de eventos',
      event_va_details: 'Detalles del evento de análisis de vídeo',
      event_cars_details: 'Detalles del evento Transporte',
      videowall: 'Videowall',
      search: 'Buscar',
      statistics_fs: 'Estadística',
      cards: 'Fichero y listas',
      cards_details: 'Tarjeta de persona',
      lists: 'Lista',
      streams_fs: 'Fuentes / Rostros y cuerpos',
      streams_va: 'Fuentes de eventos',
      handlers: 'Escenarios',
      handler_details: 'Detalles del escenario',
      tasks: 'Tareas',
      monitoring: 'Escucha',
      licenses: 'Licencias',
      notifications: 'Notificaciones',
      departments: 'Departamentos',
      users: 'Usuarios',
      roles: 'Roles',
      permission_tags: 'Etiquetas de acceso',
    },
    permission: {
      read: 'Vista',
      create: 'Creación',
      update: 'Cambiar',
      delete: 'Eliminación',
      available: 'Доступно',
    },
    visibility: {
      title: 'Zona de visibilidad',
      options: {
        basic: 'Básica',
        local: 'Local',
        global: 'Global',
      },
      descriptions: {
        basic: 'Acceso solo a listas y etiquetas de acceso asignadas a todos en el departamento.',
        local: 'Acceso a todas las listas y etiquetas de acceso del departamento (incluidas las no asignadas a todos).',
        global: 'Acceso ilimitado, independientemente de los departamentos.',
      },
    },
    reset_confirmation: {
      title: 'Restablecer cambios',
      description: '¿Está seguro de que desea restablecer todos los accesos al estado en el momento del último guardado?',
      confirm: 'Reiniciar',
      reject: 'Cancelar',
    },
  },
}) : {}) as Record<string, unknown>;
export const ptBr = (__LOCALE_PTBR__ ? ({
  [editPermissionsKeyPrefix]: {
    title: 'Acessos',
    save: 'Salvar',
    reset: 'Redefinir alterações',
    check_all: 'Selecionar tudo',
    system_objects: {
      title: 'Objetos do sistema',
      users: 'Usuários',
      roles: 'Funções',
      departments: 'Departamentos',
      permission_tags: 'Etiquetas de acesso',
      face: 'Rostos',
      card: 'Cartas',
      card_document: 'Fichas de fichas',
      stream: 'Fontes de eventos',
      group: 'Grupos de fontes',
      handler: 'Cenários',
      event_fs: 'Eventos (detecção, coincidência)',
      alarm: 'Status do evento de alarme',
      list: 'Listas (de cartões)',
      task: 'Tarefas',
      notification: 'Notificações',
      audit_log: 'Log de registro',
      audit_config: 'Configurando o registro de logs',
      session: 'Sessões de usuário',
      card_custom_field: 'Campos de cartão dinâmicos',
      event_send_rule: 'Regras para o envio de eventos para outros departamentos',
    },
    pages: {
      title: 'Interface do sistema',
      operator_panel: 'Painel do operador',
      events_fs_archive: 'Arquivo de eventos / Detecção e confluência',
      events_va_archive: 'Arquivo de eventos / Análise de vídeo',
      events_cars_archive: 'Arquivo de eventos / Transporte',
      event_fs_details: 'Detecção e correspondência de detalhes do evento',
      event_va_details: 'Detalhes do evento de análise de vídeo',
      event_cars_details: 'Detalhes do evento transporte',
      videowall: 'Painel de vídeo',
      search: 'Procurar',
      statistics_fs: 'Estatísticas',
      cards: 'Fichas e listas',
      cards_details: 'Cartão de pessoa',
      lists: 'Lista',
      streams_fs: 'Fontes / Rostos e Corpos',
      streams_va: 'Fontes de eventos',
      handlers: 'Cenários',
      handler_details: 'Detalhes do cenário',
      tasks: 'Tarefas',
      monitoring: 'Monitoramento',
      licenses: 'Licenças',
      notifications: 'Notificações',
      departments: 'Departamentos',
      users: 'Usuários',
      roles: 'Funções',
      permission_tags: 'Etiquetas de acesso',
    },
    permission: {
      read: 'Visualizar',
      create: 'Criação',
      update: 'Mudar',
      delete: 'Remoção',
      available: 'Disponível',
    },
    visibility: {
      title: 'Zona de visibilidade',
      options: {
        basic: 'Básica',
        local: 'Local',
        global: 'Global',
      },
      descriptions: {
        basic: 'Acesso apenas a listas e etiquetas de acesso atribuídas a todos no departamento.',
        local: 'Acesso a todas as listas e etiquetas de acesso do departamento (incluindo as não atribuídas a todos).',
        global: 'Acesso ilimitado, independentemente dos departamentos.',
      },
    },
    reset_confirmation: {
      title: 'Redefinir alterações',
      description: 'Tem certeza de que deseja redefinir todos os acessos ao estado no momento da última gravação?',
      confirm: 'Reiniciar',
      reject: 'Cancelar',
    },
  },
}) : {}) as Record<string, unknown>;
